import { memo, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import "./UpdateVehicleModal.css";
import defaultTruckImg from "../../assets/logo_vehicle.png";
import { firebaseUploadVehicleImage } from "../../services/firebaseUpload";
import { serviceVehiclePutUpdate } from "../../api/serviceFleetMaster";

const toDateInputValue = (dateValue) => {
  if (!dateValue) {
    return "";
  }

  /*
   * Avoid timezone shifts for values already beginning with
   * a valid YYYY-MM-DD date.
   */
  const dateMatch = String(dateValue).match(/^(\d{4}-\d{2}-\d{2})/);

  if (dateMatch) {
    return dateMatch[1];
  }

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getCurrentLocalDate = () => {
  const date = new Date();
  const timezoneOffset = date.getTimezoneOffset() * 60_000;

  return new Date(date.getTime() - timezoneOffset).toISOString().split("T")[0];
};

const normalizeVehicle = (vehicle) => ({
  vehicleCode: vehicle.vehicleCode ?? "",

  weightUnit: vehicle.weightsMax?.unit ?? "kg",
  steerMaxWeight: vehicle.weightsMax?.steer ?? "",
  driveMaxWeight: vehicle.weightsMax?.drive ?? "",
  trailerAMaxWeight: vehicle.weightsMax?.trailerA ?? "",
  trailerBMaxWeight: vehicle.weightsMax?.trailerB ?? "",

  voltage: vehicle.voltage?.value ?? "",
  voltageUnit: vehicle.voltage?.unit ?? "volt",

  fuel: vehicle.fuel?.value ?? "",
  fuelUnit: vehicle.fuel?.unit ?? "liters",
  mileage: vehicle.fuel?.mileage ?? "",

  servicedOn: toDateInputValue(vehicle.servicedOn),
  nextServiceOn: toDateInputValue(vehicle.nextServiceOn),

  imageUrl: vehicle.imageUrl ?? "",
});

const UpdateVehicleModal = ({ vehicle, onClose, onUpdate }) => {
  const fileInputRef = useRef(null);

  const [image, setImage] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");

  const [updating, setUpdating] = useState(false);

  const [originalVehicle, setOriginalVehicle] = useState(null);

  const [updatedVehicle, setUpdatedVehicle] = useState(null);

  const currentDate = getCurrentLocalDate();

  /*
   * Reset the form whenever a different vehicle is opened.
   */
  useEffect(() => {
    if (!vehicle) {
      setOriginalVehicle(null);
      setUpdatedVehicle(null);
      setImage(null);
      return;
    }

    const normalizedVehicle = normalizeVehicle(vehicle);

    setOriginalVehicle(normalizedVehicle);
    setUpdatedVehicle(normalizedVehicle);
    setImage(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [vehicle]);

  /*
   * Manage the temporary browser URL used for the image preview.
   */
  useEffect(() => {
    if (!image) {
      setImagePreviewUrl("");
      return undefined;
    }

    const previewUrl = URL.createObjectURL(image);

    setImagePreviewUrl(previewUrl);

    return () => {
      URL.revokeObjectURL(previewUrl);
    };
  }, [image]);

  const hasChanges = useMemo(() => {
    if (!originalVehicle || !updatedVehicle) {
      return false;
    }

    if (image) {
      return true;
    }

    return Object.keys(originalVehicle).some(
      (key) => originalVehicle[key] !== updatedVehicle[key],
    );
  }, [originalVehicle, updatedVehicle, image]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setUpdatedVehicle((currentVehicle) => ({
      ...currentVehicle,
      [name]: value,
    }));
  };

  const handleImageChange = (event) => {
    const selectedFile = event.target.files?.[0];

    if (!selectedFile) {
      return;
    }

    if (!selectedFile.type.startsWith("image/")) {
      toast.error("Please select a valid image file.");
      event.target.value = "";
      return;
    }

    setImage(selectedFile);
  };

  const openImagePicker = () => {
    if (updating) {
      return;
    }

    fileInputRef.current?.click();
  };

  const validateVehicle = () => {
    if (!vehicle?._id) {
      throw new Error("Invalid vehicle.");
    }

    if (!updatedVehicle?.vehicleCode?.trim()) {
      throw new Error("Registration (Rego) is required.");
    }

    if (
      !updatedVehicle.weightUnit ||
      !updatedVehicle.voltageUnit ||
      !updatedVehicle.fuelUnit
    ) {
      throw new Error("Missing measurement units.");
    }

    if (
      updatedVehicle.servicedOn &&
      updatedVehicle.nextServiceOn &&
      updatedVehicle.nextServiceOn < updatedVehicle.servicedOn
    ) {
      throw new Error(
        "Next service date cannot be before the last service date.",
      );
    }
  };

  const serverVehicleUpdate = async () => {
    if (!hasChanges) {
      toast.info("Vehicle is already up to date.");
      return;
    }

    try {
      validateVehicle();
      setUpdating(true);

      const vehicleCode = updatedVehicle.vehicleCode.trim();

      const imageUrl = null;
      // const imageUrl = image
      //   ? await firebaseUploadVehicleImage({
      //       organizationId,
      //       vehicleCode,
      //       file: image,
      //     })
      //   : updatedVehicle.imageUrl;

      const payload = {
        vehicleCode,

        steerMaxWeight: updatedVehicle.steerMaxWeight || null,

        driveMaxWeight: updatedVehicle.driveMaxWeight || null,

        trailerAMaxWeight: updatedVehicle.trailerAMaxWeight || null,

        trailerBMaxWeight: updatedVehicle.trailerBMaxWeight || null,

        weightUnit: updatedVehicle.weightUnit,

        voltage: updatedVehicle.voltage || null,
        voltageUnit: updatedVehicle.voltageUnit,

        fuel: updatedVehicle.fuel || null,
        fuelUnit: updatedVehicle.fuelUnit,
        mileage: updatedVehicle.mileage || null,

        servicedOn: updatedVehicle.servicedOn || null,

        nextServiceOn: updatedVehicle.nextServiceOn || null,

        imageUrl: imageUrl || null,
      };

      if (import.meta.env.DEV) {
        console.log("Update vehicle payload:", payload);
      }

      await serviceVehiclePutUpdate(vehicle._id, payload);

      toast.success("Vehicle updated successfully!");

      await onUpdate?.();
      onClose();
    } catch (error) {
      console.error("Update vehicle error:", error);

      toast.error(
        error instanceof Error ? error.message : "Error: Contact Trucktronix!",
      );
    } finally {
      setUpdating(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    await serverVehicleUpdate();
  };

  if (!updatedVehicle) {
    return null;
  }

  const displayedImage =
    imagePreviewUrl || updatedVehicle.imageUrl || defaultTruckImg;

  return (
    <div className="uvm-shell">
      <button
        type="button"
        className="uvm-close-btn"
        onClick={onClose}
        disabled={updating}
        aria-label="Close update vehicle form"
      >
        <i className="bi bi-x-lg" />
      </button>

      <div className="uvm-card">
        <div className="uvm-top-row">
          <div className="uvm-header">
            <div className="uvm-photo-section">
              <button
                type="button"
                className="uvm-image-wrap"
                onClick={openImagePicker}
                disabled={updating}
                aria-label="Change vehicle photo"
              >
                <img
                  src={displayedImage}
                  alt={updatedVehicle.vehicleCode || "Vehicle"}
                />

                <span className="uvm-image-overlay">
                  <i className="bi bi-camera-fill" />
                </span>
              </button>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={handleImageChange}
              />
            </div>

            <div className="uvm-header-info">
              <span className="uvm-title">Update Vehicle</span>

              <p className="uvm-subtitle">
                Edit vehicle specifications and maintenance information
              </p>

              <div className="uvm-meta">
                <p>
                  <span>Rego</span>

                  <span className="uvm-meta-value">
                    {updatedVehicle.vehicleCode}
                  </span>
                </p>

                <p>
                  <span>Status</span>

                  <span className="uvm-meta-value">
                    {hasChanges ? "Unsaved changes" : "Up to date"}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>

        <form
          id="update-vehicle-form"
          className="uvm-data-area"
          onSubmit={handleSubmit}
          noValidate
        >
          <FormSection title="Axle Capacity" icon="bi-truck">
            <FormField label="Steer Maximum" htmlFor="uvm-steer-weight">
              <input
                id="uvm-steer-weight"
                type="number"
                name="steerMaxWeight"
                min="0"
                step="any"
                placeholder="0"
                value={updatedVehicle.steerMaxWeight}
                onChange={handleChange}
                disabled={updating}
              />
            </FormField>

            <FormField label="Drive Maximum" htmlFor="uvm-drive-weight">
              <input
                id="uvm-drive-weight"
                type="number"
                name="driveMaxWeight"
                min="0"
                step="any"
                placeholder="0"
                value={updatedVehicle.driveMaxWeight}
                onChange={handleChange}
                disabled={updating}
              />
            </FormField>

            <FormField label="Trailer A" htmlFor="uvm-trailer-a-weight">
              <input
                id="uvm-trailer-a-weight"
                type="number"
                name="trailerAMaxWeight"
                min="0"
                step="any"
                placeholder="0"
                value={updatedVehicle.trailerAMaxWeight}
                onChange={handleChange}
                disabled={updating}
              />
            </FormField>

            <FormField label="Trailer B" htmlFor="uvm-trailer-b-weight">
              <input
                id="uvm-trailer-b-weight"
                type="number"
                name="trailerBMaxWeight"
                min="0"
                step="any"
                placeholder="0"
                value={updatedVehicle.trailerBMaxWeight}
                onChange={handleChange}
                disabled={updating}
              />
            </FormField>
          </FormSection>

          <FormSection title="Specifications" icon="bi-sliders">
            <FormField label="Weight Unit" htmlFor="uvm-weight-unit">
              <select
                id="uvm-weight-unit"
                name="weightUnit"
                value={updatedVehicle.weightUnit}
                onChange={handleChange}
                disabled={updating}
              >
                <option value="kg">Kilograms (kg)</option>

                <option value="lb">Pounds (lb)</option>
              </select>
            </FormField>

            <FormField label="Fuel Capacity" htmlFor="uvm-fuel">
              <input
                id="uvm-fuel"
                type="number"
                name="fuel"
                min="0"
                step="any"
                placeholder="0"
                value={updatedVehicle.fuel}
                onChange={handleChange}
                disabled={updating}
              />
            </FormField>

            <FormField label="Fuel Unit" htmlFor="uvm-fuel-unit">
              <select
                id="uvm-fuel-unit"
                name="fuelUnit"
                value={updatedVehicle.fuelUnit}
                onChange={handleChange}
                disabled={updating}
              >
                <option value="liters">Litres</option>

                <option value="gallons">Gallons</option>
              </select>
            </FormField>

            <FormField label="Mileage" htmlFor="uvm-mileage">
              <input
                id="uvm-mileage"
                type="number"
                name="mileage"
                min="0"
                step="any"
                placeholder="Maximum kilometres"
                value={updatedVehicle.mileage}
                onChange={handleChange}
                disabled={updating}
              />
            </FormField>

            <FormField label="Working Voltage" htmlFor="uvm-voltage">
              <input
                id="uvm-voltage"
                type="number"
                name="voltage"
                min="0"
                step="any"
                placeholder="0"
                value={updatedVehicle.voltage}
                onChange={handleChange}
                disabled={updating}
              />
            </FormField>

            <FormField label="Voltage Unit" htmlFor="uvm-voltage-unit">
              <select
                id="uvm-voltage-unit"
                name="voltageUnit"
                value={updatedVehicle.voltageUnit}
                onChange={handleChange}
                disabled={updating}
              >
                <option value="volt">Volts</option>
              </select>
            </FormField>
          </FormSection>

          <FormSection title="Scale Service" icon="bi-calendar">
            <FormField label="Last Serviced" htmlFor="uvm-serviced-on">
              <input
                id="uvm-serviced-on"
                type="date"
                name="servicedOn"
                max={currentDate}
                value={updatedVehicle.servicedOn}
                onChange={handleChange}
                disabled={updating}
              />
            </FormField>

            <FormField label="Next Service Due" htmlFor="uvm-next-service-on">
              <input
                id="uvm-next-service-on"
                type="date"
                name="nextServiceOn"
                min={currentDate}
                value={updatedVehicle.nextServiceOn}
                onChange={handleChange}
                disabled={updating}
              />
            </FormField>
          </FormSection>
        </form>
      </div>

      <footer className="uvm-footer">
        <div className="uvm-footer-inner">
          <div className="uvm-actions">
            <button
              type="submit"
              form="update-vehicle-form"
              className="uvm-submit-cta"
              disabled={updating}
            >
              {updating ? (
                <>
                  <span className="uvm-spinner" aria-hidden="true" />

                  <span>Updating...</span>
                </>
              ) : (
                <span>Save Changes</span>
              )}
            </button>

            <button
              type="button"
              className="uvm-cancel-btn"
              onClick={onClose}
              disabled={updating}
            >
              Cancel
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

const FormSection = ({ title, icon, children }) => (
  <section className="uvm-section">
    <header>
      <i className={`bi ${icon}`} />
      <span>{title}</span>
    </header>

    <div className="uvm-grid">{children}</div>
  </section>
);

const FormField = ({ label, htmlFor, urgent = false, children }) => (
  <div className={`uvm-field ${urgent ? "urgent" : ""}`}>
    <label htmlFor={htmlFor}>{label}</label>

    <div className="uvm-control">{children}</div>
  </div>
);

export default memo(UpdateVehicleModal);
